import { v4 as uuidv4 } from 'uuid';

/**
 * Состояние режима агента, меняющего данные.
 *
 * Отдельная ветка от `chatReducer` намеренно: тот чат отвечает на вопросы по
 * документации, этот создаёт и правит сущности. Общая история путала бы два
 * разговора и подсовывала бы одному контекст другого. Открыта панель или нет —
 * общее на оба режима и живёт в `chatReducer`.
 */

// Actions
export const AGENT_RUN_START = 'mcpAgent/runStart';
export const AGENT_STEP = 'mcpAgent/step';
export const AGENT_STEP_RESULT = 'mcpAgent/stepResult';
export const AGENT_CONFIRM = 'mcpAgent/confirm';
export const AGENT_CONFIRM_ANSWER = 'mcpAgent/confirmAnswer';
export const AGENT_ANSWER = 'mcpAgent/answer';
export const AGENT_USAGE = 'mcpAgent/usage';
export const AGENT_ERROR = 'mcpAgent/error';
export const AGENT_RUN_FINISH = 'mcpAgent/runFinish';
export const AGENT_SET_ROUTER = 'mcpAgent/setRouter';
export const AGENT_SET_MODEL = 'mcpAgent/setModel';
export const AGENT_CLEAR = 'mcpAgent/clear';

// Action creators
export const startAgentRun = (prompt) => ({ type: AGENT_RUN_START, payload: prompt });
export const addAgentStep = (callId, name, args) => ({
  type: AGENT_STEP,
  payload: { callId, name, args },
});
export const resolveAgentStep = (callId, isOk, preview) => ({
  type: AGENT_STEP_RESULT,
  payload: { callId, isOk, preview },
});
export const askAgentConfirm = (request) => ({ type: AGENT_CONFIRM, payload: request });
/** `error` — ответ не дошёл до сервера, кнопки снова доступны. */
export const answerAgentConfirmStep = (callId, error = '') => ({
  type: AGENT_CONFIRM_ANSWER,
  payload: { callId, error },
});
export const setAgentAnswer = (text) => ({ type: AGENT_ANSWER, payload: text });
export const setAgentUsage = (usage) => ({ type: AGENT_USAGE, payload: usage });
export const setAgentError = (message) => ({ type: AGENT_ERROR, payload: message });
export const finishAgentRun = () => ({ type: AGENT_RUN_FINISH });
export const setAgentRouter = (marker) => ({ type: AGENT_SET_ROUTER, payload: marker });
export const setAgentModel = (model) => ({ type: AGENT_SET_MODEL, payload: model });
export const clearAgent = () => ({ type: AGENT_CLEAR });

const initialState = {
  /** Завершённые ходы: `{ id, prompt, answer, steps, usage, isError }`. */
  turns: [],
  /** Шаги текущего прогона — лента «ищу операцию», «создаю», «проверяю». */
  steps: [],
  prompt: '',
  isRunning: false,
  /** Расход прогона, пришедший до закрытия хода — см. `AGENT_USAGE`. */
  pendingUsage: null,
  routerMarker: '',
  model: '',
};

/**
 * Отмечает шаг завершённым по идентификатору вызова.
 *
 * Именно по идентификатору, а не по имени инструмента: агент запускает
 * несколько вызовов разом, в том числе одного инструмента, и отвечают они не в
 * том порядке, в каком начинались. При поиске по имени часть шагов навсегда
 * оставалась бы мигать как «выполняется».
 */
const resolveStep = (steps, callId, isOk, preview) => steps.map((step) => (
  step.callId === callId
    ? { ...step, isOk, preview, isDone: true, confirm: null }
    : step
));

/** Меняет запрос подтверждения у шага с этим вызовом. */
const patchConfirm = (steps, callId, patch) => steps.map((step) => (
  step.callId === callId ? { ...step, confirm: patch(step.confirm) } : step
));

/** Закрывает ход: то, что успело накопиться, уходит в историю целиком. */
const closeTurn = (state, extra) => ({
  ...state,
  isRunning: false,
  steps: [],
  prompt: '',
  pendingUsage: null,
  turns: [
    ...state.turns,
    {
      id: uuidv4(),
      prompt: state.prompt,
      steps: state.steps,
      answer: '',
      usage: state.pendingUsage,
      isError: false,
      ...extra,
    },
  ],
});

export default function mcpAgentReducer(state = initialState, action) {
  switch (action.type) {
  case AGENT_RUN_START:
    return { ...state, isRunning: true, steps: [], prompt: action.payload };

  case AGENT_STEP:
    return {
      ...state,
      steps: [
        ...state.steps,
        { ...action.payload, isDone: false, isOk: null, preview: '' },
      ],
    };

  case AGENT_STEP_RESULT: {
    const { callId, isOk, preview } = action.payload;
    return { ...state, steps: resolveStep(state.steps, callId, isOk, preview) };
  }

  case AGENT_CONFIRM:
    return {
      ...state,
      steps: patchConfirm(state.steps, action.payload.callId, () => ({
        ...action.payload,
        isSent: false,
        error: '',
      })),
    };

  case AGENT_CONFIRM_ANSWER: {
    const { callId, error } = action.payload;
    return {
      ...state,
      steps: patchConfirm(state.steps, callId, (confirm) => (
        confirm ? { ...confirm, isSent: !error, error } : confirm
      )),
    };
  }

  case AGENT_ANSWER:
    return closeTurn(state, { answer: action.payload });

  case AGENT_ERROR:
    return closeTurn(state, { answer: action.payload, isError: true });

  case AGENT_USAGE: {
    // Расход приходит в двух разных местах. При удачном прогоне — после ответа,
    // когда ход уже закрыт: дописываем его в этот ход. При падении и отмене —
    // наоборот, раньше ошибки, потому что сервер пишет расход в `finally`:
    // придерживаем до закрытия хода, иначе он приклеится к предыдущему.
    if (state.isRunning) {
      return { ...state, pendingUsage: action.payload };
    }
    const last = state.turns[state.turns.length - 1];
    if (!last) {
      return state;
    }
    return {
      ...state,
      turns: [...state.turns.slice(0, -1), { ...last, usage: action.payload }],
    };
  }

  case AGENT_RUN_FINISH:
    // Поток закрылся, а ответа не было — значит прогон оборвали. Ход всё
    // равно закрываем, иначе кнопка запуска останется заблокированной.
    return state.isRunning ? closeTurn(state, { answer: '' }) : state;

  case AGENT_SET_ROUTER:
    return { ...state, routerMarker: action.payload };

  case AGENT_SET_MODEL:
    return { ...state, model: action.payload };

  case AGENT_CLEAR:
    return {
      ...initialState,
      routerMarker: state.routerMarker,
      model: state.model,
    };

  default:
    return state;
  }
}
